import http from "../../httpService"
import { normalizePermissionList } from "@lib/permissionNormalize"

// --- DTOs ---

export interface PagedBundleRequestDto {
  maxResultCount?: number
  skipCount?: number
  keyword?: string
  isActive?: boolean | null
  feGroupCode?: string | null
}

export interface BundleListDto {
  id: string
  name: string
  displayName: string
  description: string
  permissionCount: number
  isActive: boolean
  feGroupCode: string | null
  isDraft: boolean
  permissionNames?: string[]
}

export interface CreateBundleDto {
  name: string
  displayName?: string
  description?: string
  feGroupCode?: string
  permissionNames: string[]
}

export interface UpdateBundleDto {
  id: string
  name: string
  displayName?: string
  description?: string
  feGroupCode?: string
  permissionNames: string[]
}

export interface SaveRolePermissionInput {
  roleId: number
  mode: BundleAssignMode
  bundleIds: string[]
  keptPermissionNames: string[]
  permissionNames: string[]
}

export enum BundleAssignMode {
  Business = 0,
  Advanced = 1,
}

export interface BundleGroupDto {
  feGroupCode: string
  groupDisplayName: string
  bundles: BundleListDto[]
}

export interface BundleStateDto {
  bundleId: string
  bundleName: string
  state: BundleState
}

export enum BundleState {
  None = 0,
  Partial = 1,
  Full = 2,
}

export interface PermissionDto {
  id: number
  name: string
  displayName?: string
  parentName?: string
}

export interface GetRoleBundleViewOutput {
  role: {
    id: number
    name: string
    displayName: string
    description: string
    isStatic: boolean
  }
  grantedPermissionNames: string[]
  bundlesByGroup: BundleGroupDto[]
  assignedBundleIds: string[]
  bundleStates: BundleStateDto[]
  orphanPermissions: PermissionDto[]
  // Number of defined permissions not yet assigned to any active bundle (Req 2.3).
  orphanCount: number
  // Module codes that have at least one permission in the source (Req 2.1, audit).
  coveredModuleCodes: string[]
  // Exactly one draft bundle proposed per module lacking an active bundle (Req 2.6).
  suggestedDrafts: BundleDraftDto[]
}

export interface AddOrphanInput {
  bundleId: string
  permissionNames: string[]
}

export interface PagedResultDto<T> {
  totalCount: number
  items: T[]
}

// --- Migration DTOs ---

export interface BundleDraftDto {
  name: string
  displayName: string
  permissionNames: string[]
  feGroupCode: string | null
}

// FeBeSyncReportDto is retained: consumed by the pure permissionTreeBuilder util (Req 1.6).
export interface FeBeSyncReportDto {
  feOnlyPermissions: string[]
  beOnlyPermissions: string[]
  totalFePermissions: number
  totalBePermissions: number
}

// --- Service ---

class BundleService {
  // ========================
  // Bundle API (api/services/app/Bundle/*)
  // ========================

  public async getAll(
    input: PagedBundleRequestDto
  ): Promise<PagedResultDto<BundleListDto>> {
    const result = await http.get("api/services/app/Bundle/GetAll", {
      params: input,
    })
    return result.data.result
  }

  // Flat permission list used by the bundle create/edit permission picker (BundleFormDrawer).
  public async getPermissionTree(): Promise<PermissionDto[]> {
    try {
      const result = await http.get(
        "api/services/app/Bundle/GetPermissionTree",
        { timeout: 10000 }
      )
      return normalizePermissionList(result.data.result.items || [])
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error
      }
      throw new Error("Failed to load permission tree")
    }
  }

  public async create(input: CreateBundleDto): Promise<BundleListDto> {
    const result = await http.post("api/services/app/Bundle/Create", input)
    return result.data.result
  }

  public async update(input: UpdateBundleDto): Promise<BundleListDto> {
    const result = await http.put("api/services/app/Bundle/Update", input)
    return result.data.result
  }

  public async delete(input: { id: string }): Promise<void> {
    await http.delete("api/services/app/Bundle/Delete", {
      params: input,
    })
  }

  public async clone(input: { id: string }): Promise<BundleListDto> {
    const result = await http.post("api/services/app/Bundle/Clone", input)
    return result.data.result
  }

  public async getForEdit(id: string): Promise<UpdateBundleDto> {
    const result = await http.get("api/services/app/Bundle/GetForEdit", {
      params: { id },
    })
    return result.data.result
  }

  public async getRoleBundleView(
    roleId: number
  ): Promise<GetRoleBundleViewOutput> {
    const result = await http.get("api/services/app/Bundle/GetRoleBundleView", {
      params: { roleId },
    })
    return result.data.result
  }

  public async saveRolePermissions(
    input: SaveRolePermissionInput
  ): Promise<void> {
    await http.post("api/services/app/Bundle/SaveRolePermissions", input)
  }

  public async getOrphans(): Promise<PermissionDto[]> {
    const result = await http.get("api/services/app/Bundle/GetOrphans")
    return normalizePermissionList(result.data.result.items || [])
  }

  public async addPermissionsToBundle(input: AddOrphanInput): Promise<void> {
    await http.post("api/services/app/Bundle/AddPermissionsToBundle", input)
  }

  // ========================
  // PermissionMigration API (api/services/app/PermissionMigration/*)
  // ========================

  public async scanForBundleDrafts(): Promise<BundleDraftDto[]> {
    const result = await http.post(
      "api/services/app/PermissionMigration/ScanForBundleDrafts"
    )
    return result.data.result
  }

  public async getDrafts(): Promise<BundleDraftDto[]> {
    const result = await http.get(
      "api/services/app/PermissionMigration/GetDrafts"
    )
    return result.data.result
  }

  public async approveDraft(input: { name: string }): Promise<void> {
    await http.post(
      "api/services/app/PermissionMigration/ApproveDraft",
      input
    )
  }
}

export default new BundleService()
